import type { AccountingData } from "@@/shared/financials/accounting/accounting-data";
import { percentageToHuman, roundPercentage } from "@@/shared/percentage";
import type { Price} from "@@/shared/prices/price";
import { priceFactory } from "@@/shared/prices/price";
import { subtractFromPrice } from "@@/shared/prices/subtract-from-price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import { vatRatesSweden } from "@@/shared/prices/vat-rate-sweden";
import type { ProductId } from "@@/shared/products/product-id";
import { repeat } from "@@/utilities/repeat";
import type { Transaction } from "../transactions";
import { getDimensions } from "./sie-generator-transaction-lines-sales";

function generateTransactionLines_VAT(
    transaction: Transaction,
    accountingData: AccountingData,
    refund: number,
) {
    const exclVatRows = transaction.items
        .filter(item => item.pricePerItem.amountIncludingVat !== 0)

        .map(item => {
            switch (!!item.optionalId) {
                case true: {
                    const transactionItems = transaction.items
                        .filter(
                            transactionItem =>
                                item.orderItemId ===
                                    transactionItem.orderItemId &&
                                !transactionItem.optionalId,
                        )
                        .filter(Boolean);

                    if (transactionItems.length > 1) {
                        throw new Error("Optional parent couldn't be found");
                    }

                    // Item is an OPTIONAL
                    const totalPrice = sumPrices(
                        repeat(item.pricePerItem, transactionItems[0].quantity),
                    );

                    const line = vat_Line(
                        accountingData,
                        totalPrice,
                        refund,
                        transaction,
                        transactionItems[0]?.productId,
                    );
                    return line;
                }
                case false: {
                    // Item must be a product or old fee
                    // Find optionals for item
                    const optionals = transaction.items
                        .filter(
                            transactionItem =>
                                item.orderItemId ===
                                    transactionItem.orderItemId &&
                                transactionItem.optionalId,
                        )
                        .filter(Boolean);

                    // Calculate price per product without optional pricing
                    const productPricePerItem = subtractFromPrice(
                        item.pricePerItem,
                        optionals.map(optional =>
                            sumPrices(
                                repeat(
                                    optional.pricePerItem,
                                    optional.quantity,
                                ),
                            ),
                        ),
                    );
                    const totalPrice = sumPrices(
                        repeat(productPricePerItem, item.quantity),
                    );

                    const line = vat_Line(
                        accountingData,
                        totalPrice,
                        refund,
                        transaction,
                        item.productId,
                    );
                    return line;
                }
            }
        });

    return exclVatRows;
}
//Due to early miscalculation the vat could be calculated wrong when doing sums.
const recalculateVat = (price: Price) => {
    const vatRounded = price.vatRounded ?? roundPercentage(price.vat);

    if (vatRatesSweden.includes(vatRounded)) {
        return priceFactory({
            amountIncludingVat: price.amountIncludingVat,
            vat: vatRounded,
        });
    } else {
        // eslint-disable-next-line no-console
        console.log("VAT IS NOT SWEDISH", price);
        return price;
    }
};

const vat_Line = (
    accountingData: AccountingData,
    totalPrice: Price,
    refund: number,
    transaction: Transaction,
    productId?: ProductId,
) => {
    const price = recalculateVat(totalPrice);
    const accountNumber =
        accountingData.vatAccountMapping[
            percentageToHuman(price.vatRounded ?? price.vat)
        ] || accountingData.fallbackVatAccount;
    const hasAccountNumber =
        typeof accountNumber === "string"
            ? accountNumber.trim()
            : typeof accountNumber === "number";

    const dimensions = getDimensions(productId, accountNumber, accountingData);

    const dimensionString =
        dimensions.length > 0
            ? ` { ${dimensions
                  .map(d => `"${d.dimId}" "${d.value}"`)
                  .join(" ")} }`
            : "{ }";

    const line = `#TRANS ${
        hasAccountNumber ? accountNumber : "VAT_SAKNAS"
    } ${dimensionString} ${(-1 * refund * price.vatAmount) / 100} "" "${
        transaction.orderId
    }"`;
    return line;
};

export { generateTransactionLines_VAT, recalculateVat };
